import React, { useEffect, useState } from "react";
import { Button, Form, Confirm, Popup, Message } from "semantic-ui-react";
import MESSAGE from "config/message.json";
import { generatePath, useHistory } from "react-router-dom";
import routes from "utils/routes";
import { SeoPreferencesForm } from "components/lib/Post/SeoPreferencesForm";
import { PostContentForm } from "components/lib/Post/PostContentForm";
import { newsService } from "domain/service";
import { Formik } from "formik";
import dayjs from "dayjs";
import { newsRepository } from "domain/repository";
import { PostPreview, PostTypes } from "components/lib/Post/PostPreview";
import { SelectMediaModal } from "components/lib/SelectMediaModal";
import Post from "domain/model/Post";
import { usePostSchema } from "hooks/usePostSchema";
import { usePostMenu } from "hooks/usePostMenu";
import { useAllMediaUrls } from "hooks/useAllMediaUrls";
import { useNewsSubCategory } from "components/Post/News/useNewsSubCategory";
import { useNewsTemplate } from "components/Post/News/useNewsTemplate";
import { RavenLoader } from "components/lib/RavenLoader";

type Props = {
  isAgency: boolean;
  storeId: string;
  filePath: string;
};

export const EditFormContainer: React.FC<Props> = ({
  isAgency,
  storeId,
  filePath,
}) => {
  const [editNews, setEditNews] = useState<Post | null>(null);
  const [editContent, setEditContent] = useState<string | null>(null);
  const { template } = useNewsTemplate({ isAgency, storeId });
  const { subCategories, NON_SUB_CATEGORY_VALUE } = useNewsSubCategory({
    isAgency,
    storeId,
  });
  const { mediaUrls, refetchMediaUrls } = useAllMediaUrls({
    isAgency,
    storeId,
    imageOnly: true,
  });
  const { activeMenu, MenuItems, renderMenu } = usePostMenu();
  const { validationSchema } = usePostSchema();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [openThumbnailModal, setOpenThumbnailModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const newsIndexUrl = isAgency
    ? generatePath(routes.agency.newsIndex.pathname, {
        storeId,
      })
    : routes.store.newsIndex.pathname;
  const history = useHistory();

  useEffect(() => {
    const fetchEditNews = () => {
      newsService.fetchEditNews(isAgency, storeId, filePath).then((res) => {
        setEditNews(res.news);
        setEditContent(res.content ?? "");
      });
    };
    fetchEditNews();
  }, [filePath, isAgency, storeId]);

  const deletePost = () => {
    setDeleting(true);
    newsRepository
      .deletePost(isAgency, storeId, filePath)
      .then(() => {
        history.push(newsIndexUrl);
      })
      .finally(() => {
        setDeleting(false);
      });
  };

  const renderContent = () => {
    if (activeMenu === MenuItems.POST_CONTENT) {
      return (
        <PostContentForm
          subCategories={subCategories}
          mediaUrls={mediaUrls}
          refetchMediaUrls={refetchMediaUrls}
          isEdit
          isPublished={editNews!.isPublished}
        />
      );
    }
    if (activeMenu === MenuItems.POST_PREVIEW) {
      return <PostPreview template={template} postType={PostTypes.NEWS} />;
    }
    if (activeMenu === MenuItems.SEO_PREFERENCES) {
      return (
        <SeoPreferencesForm
          mediaUrls={mediaUrls}
          refetchMediaUrls={refetchMediaUrls}
        />
      );
    }
  };

  if (editNews === null || editContent === null) {
    return <RavenLoader active />;
  }

  return (
    <>
      <RavenLoader active={deleting} content="削除中" />
      <Confirm
        open={openDeleteModal}
        content="この投稿を削除してもよろしいですか？"
        onCancel={() => setOpenDeleteModal(false)}
        onConfirm={deletePost}
      />
      <Formik
        initialValues={{
          postContent: {
            title: editNews.title,
            postDate: dayjs(editNews.createdAt).toDate(),
            subCategoryValue: editNews.subcategory ?? NON_SUB_CATEGORY_VALUE,
            slug: editNews.slug,
            content: editContent,
            thumbnailUrl: editNews.metadata.thumbnailUrl,
          },
          seoPreferences: editNews.metadata.seoPreferences,
          isDraft: false,
        }}
        validationSchema={validationSchema}
        onSubmit={(
          { postContent, seoPreferences, isDraft },
          { setFieldValue, setFieldError, resetForm, setSubmitting }
        ) => {
          setErrorMessage(null);
          newsRepository
            .updateNews(
              isAgency,
              storeId,
              filePath,
              {
                postContent: {
                  title: postContent.title,
                  slug: postContent.slug,
                  content: postContent.content,
                  postDate: postContent.postDate,
                  thumbnailUrl: postContent.thumbnailUrl,
                  subcategory:
                    postContent.subCategoryValue === NON_SUB_CATEGORY_VALUE
                      ? ""
                      : postContent.subCategoryValue,
                },
                seoPreferences,
              },
              isDraft
            )
            .then(() => {
              resetForm();
              history.push(newsIndexUrl);
            })
            .catch((e: string) => {
              if (e === `{:filepath=>["has already been taken"]}`) {
                setFieldError(
                  "postContent.slug",
                  MESSAGE.ERROR.POST.ALREADY_USE_SLUG
                );
              } else if (e === `{:slug=>["is invalid"]}`) {
                setFieldError(
                  "postContent.slug",
                  MESSAGE.ERROR.POST.INVALID_SLUG
                );
              } else {
                setErrorMessage(MESSAGE.ERROR.POST.EDIT_ERROR);
              }
            })
            .finally(() => {
              setSubmitting(false);
            });
        }}
      >
        {({ handleSubmit, setFieldValue, isSubmitting }) => (
          <Form onSubmit={handleSubmit}>
            <div
              css={{
                margin: "16px 0",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                css={{ margin: "0 16px 0 0 !important" }}
                content="サムネイル"
                icon="image"
                primary
                onClick={(e) => {
                  setOpenThumbnailModal(true);
                  e.preventDefault();
                }}
              />
              <SelectMediaModal
                title="サムネイルの設定"
                mediaUrls={mediaUrls ?? []}
                onUploaded={refetchMediaUrls}
                open={openThumbnailModal}
                handleMediaClick={(url) => {
                  setFieldValue("postContent.thumbnailUrl", url);
                  setOpenThumbnailModal(false);
                }}
                close={() => setOpenThumbnailModal(false)}
              />
              <div css={{ margin: "0 16px 0 0" }}>
                {editNews.isPublished ? (
                  <Popup
                    content="このお知らせを非公開に変更して下書きとして保存します"
                    position="top center"
                    trigger={
                      <Button
                        positive
                        icon="pencil alternate"
                        type="submit"
                        content="非公開"
                        onClick={() => {
                          setFieldValue("isDraft", true);
                        }}
                        loading={isSubmitting}
                      />
                    }
                  />
                ) : (
                  <Button
                    positive
                    icon="pencil alternate"
                    type="submit"
                    content="下書き保存"
                    onClick={() => {
                      setFieldValue("isDraft", true);
                    }}
                    loading={isSubmitting}
                  />
                )}
              </div>
              <Button
                positive
                icon="paper plane"
                type="submit"
                content={editNews.isPublished ? "更新" : "公開"}
                onClick={() => {
                  setFieldValue("isDraft", false);
                }}
                css={{ margin: "0 16px 0 0 !important" }}
                loading={isSubmitting}
              />
              <Button
                negative
                content="削除"
                onClick={(e) => {
                  setOpenDeleteModal(true);
                  e.preventDefault();
                }}
              />
            </div>
            {renderMenu()}
            {errorMessage ? (
              <Message negative content={errorMessage} size="small" />
            ) : null}
            {renderContent()}
          </Form>
        )}
      </Formik>
    </>
  );
};
